import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState(() => {
        const saved = localStorage.getItem('wayside-cart');
        return saved ? JSON.parse(saved) : [];
    });

    useEffect(() => {
        localStorage.setItem('wayside-cart', JSON.stringify(items));
    }, [items]);

    const addItem = (product, quantity = 1, options = {}) => {
        const gradeOptions = Array.isArray(product.grade_options) ? product.grade_options.filter(Boolean) : [];
        const selectedGrade = options.selected_grade || product.selected_grade || product.default_grade || (gradeOptions.length > 0 ? gradeOptions[0] : null);
        setItems(prev => {
            const existing = prev.find(item => item.product.id === product.id && (item.selected_grade || null) === (selectedGrade || null));
            if (existing) {
                return prev.map(item =>
                    item.product.id === product.id && (item.selected_grade || null) === (selectedGrade || null)
                        ? { ...item, quantity: item.quantity + quantity }
                        : item
                );
            }
            return [...prev, { product, quantity, selected_grade: selectedGrade }];
        });
    };

    const removeItem = (productId, selectedGrade = null) => {
        setItems(prev => prev.filter(item => !(item.product.id === productId && (item.selected_grade || null) === (selectedGrade || null))));
    };

    const updateQuantity = (productId, quantity, selectedGrade = null) => {
        if (quantity <= 0) {
            removeItem(productId, selectedGrade);
            return;
        }
        setItems(prev =>
            prev.map(item =>
                item.product.id === productId && (item.selected_grade || null) === (selectedGrade || null)
                    ? { ...item, quantity }
                    : item
            )
        );
    };

    const clearCart = () => {
        setItems([]);
    };

    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
    const total = items.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);

    return (
        <CartContext.Provider value={{
            items,
            addItem,
            removeItem,
            updateQuantity,
            clearCart,
            itemCount,
            total
        }}>
            {children}
        </CartContext.Provider>
    );
};
